import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { DataElement, Template, TemplateCode, TemplateType } from '@store/user/user.model';
import {
  Attribute,
  AttributesCategory,
  AttributeTemplateData
} from '@shared/components/categories-attributes-selector/categories-attributes-selector.model';
import { LabelType, MAX_NAME_LENGTH, MULTI_COUNTY, OperatorValue, SelectionPropertiesItem } from '@store/search-board/search-board.model';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ConfirmationService } from '@shared/services/confirmation.service';
import { combineLatest, Observable, of } from 'rxjs';
import { ConfirmationMessageType, ConfirmationModalActionType, ConfirmationMySearchMessageType } from '@shared/models/modal.model';
import { select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import { selectCurrentCounties, selectIsRegionMultiState, selectMySearchesTemplates, selectTemplate } from '@store/user/user.selector';
import { selectSelectedSearch, selectSelectedSearchModal } from '@store/search-board/search-board.selector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import {
  ApplyEditMySearch,
  SaveSelectedEditSearchFromModal,
  SetMySearchLastLoadedTemplate,
  SetSelectedMySearch,
  SetSelectedMySearchModal,
} from '@store/search-board/search-board.actions';
import { RemoveCustomMySearch, UpdateCustomMySearch } from '@store/user/user.actions';
import { FieldCode, STATE_TOKEN_NUMBER } from '@root/search/models/display-fields.constant';
import { MixpanelService } from '@shared/services/mixpanel.service';

@UntilDestroy()
@Component({
  selector: 'rlst-edit-search',
  templateUrl: './edit-search.component.html',
  styleUrls: ['./edit-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditSearchComponent implements OnInit {
  public mySearchType: UntypedFormGroup;
  public editNameControl: UntypedFormControl;
  public isAdmin: boolean = false;
  public categories: Array<AttributesCategory> = [];
  public selectedCategory: AttributesCategory = null;
  public selectedAttributes: Array<SelectionPropertiesItem> = [];
  public maxNumber: number = 30; // @TODO: change it in next integration task
  private defaultAttributes: Array<Attribute> = [];
  public allAttributes: Array<Attribute> = [];
  public selectItemList: Array<Template>;
  private existingTemplateNames: Array<LabelType> = [];
  public displayedAttributes: Array<Attribute> = [];
  public isMobileNextStep: boolean = false;
  public attributeTemplateData: Array<AttributeTemplateData> = [];
  public selectedEditSearch$: Observable<Template>;
  public selectedEditSearch: Template;
  public readonly templateType: typeof TemplateType = TemplateType;
  public maxNameLength: typeof MAX_NAME_LENGTH = MAX_NAME_LENGTH;
  public nameExist$: Observable<boolean>;
  public emptyName: boolean = false;
  private currentCounty: string;
  private isRegionMultiState: boolean;

  constructor(private readonly dialogRef: MatDialogRef<EditSearchComponent>,
              private readonly confirmationService: ConfirmationService,
              private readonly cdr: ChangeDetectorRef,
              private readonly store$: Store<AppState>,
              private readonly fb: UntypedFormBuilder,
              private readonly mixpanelService: MixpanelService) {

    this.store$.pipe(
      select(selectCurrentCounties),
      untilDestroyed(this)
    ).subscribe((currentCounty: string) => this.currentCounty = currentCounty);

    this.store$.pipe(
      select(selectIsRegionMultiState),
      untilDestroyed(this)
    ).subscribe((isRegionMultiState: boolean) => this.isRegionMultiState = isRegionMultiState);

    this.mySearchType = this.fb.group({
      searchType: [{value: TemplateType.FORM, disabled: true}]
    });

    this.editNameControl = new UntypedFormControl('');

    this.editNameControl
      .valueChanges
      .pipe(
        untilDestroyed(this),
        tap(() => {
          if (this.emptyName) {
            this.emptyName = false;
          }
        }),
        filter((value: string) => value.length > MAX_NAME_LENGTH)
      )
      .subscribe((value: string) => this.editNameControl.setValue(value.substr(0, MAX_NAME_LENGTH)));

    combineLatest(
      [
        this.store$.pipe(select(selectMySearchesTemplates)),
        this.store$.pipe(select(selectTemplate(TemplateCode.TMPL_MY_SEARCH_DEFAULT))),
        this.store$.pipe(select(selectTemplate(TemplateCode.TMPL_MY_SEARCH_ALL_ATTRIBUTES)))
      ]
    ).pipe(
      untilDestroyed(this),
      map(([mySearchesTemplates, mySearchDefaultTemplate, searchAllAttributes]) => ({
        mySearchesTemplates: [mySearchDefaultTemplate, ...mySearchesTemplates],
        searchAllAttributes
      })),
    ).subscribe(({mySearchesTemplates, searchAllAttributes}) => this.mapData(mySearchesTemplates, searchAllAttributes));
  }

  ngOnInit(): void {
    this.selectedEditSearch$ = this.store$.pipe(select(selectSelectedSearchModal));

    this.selectedEditSearch$
      .pipe(untilDestroyed(this))
      .subscribe((template: Template) => this.openSearch(template));

    this.nameExist$ = combineLatest(
      [
        this.mySearchType.valueChanges.pipe(startWith(this.mySearchType.getRawValue())),
        this.editNameControl.valueChanges.pipe(startWith(this.editNameControl.value))
      ]
    ).pipe(
      untilDestroyed(this),
      map(([{searchType}, searchName]: [{ searchType: string }, string]) => {
        if (!searchName) {
          return false;
        }

        if (this.selectedEditSearch && this.selectedEditSearch.type !== searchType) {
          return this.existingTemplateNames.some((el: LabelType) => el.label === searchName && el.type === searchType);
        }
        return this.existingTemplateNames
          .filter((el: LabelType) => el.label !== (this.selectedEditSearch && this.selectedEditSearch.label || ''))
          .some((el: LabelType) => el.label === searchName && el.type === searchType);
      })
    );
  }

  private mapData(mySearchesTemplates: Array<Template>, searchAllAttributes: Template): void {
    this.selectItemList = mySearchesTemplates;
    this.existingTemplateNames = mySearchesTemplates.map((template: Template) => ({
      label: template.label,
      type: template.type
    }));

    const categories: Array<AttributesCategory> = [];
    const allAttributes: Array<Attribute> = [];

    searchAllAttributes.childTemplateList.forEach((searchElement: Template) => {
      if (searchElement.dataElementList && searchElement.dataElementList.length > 0) {
        categories.push({
          name: searchElement.label,
          id: searchElement.templateCode
        });
      }

      searchElement.dataElementList.forEach((dataElement: DataElement) => {
        const isDisabledByMultiCounty: boolean = (this.currentCounty === MULTI_COUNTY) && !dataElement.multiCounty;
        const isDisabledByMultiState: boolean = this.isRegionMultiState && (dataElement.tokenNumber === STATE_TOKEN_NUMBER);

        allAttributes.push({
          label: dataElement.label,
          fieldCode: dataElement.fieldCode,
          name: searchElement.templateCode,
          disabled: isDisabledByMultiCounty || isDisabledByMultiState,
          disabledTooltip: isDisabledByMultiState ?
            'This attribute is not available when two or more States are selected' :
            isDisabledByMultiCounty ? 'Attribute is unavailable when multiple counties are selected' : '',
          selected: false
        });

        this.attributeTemplateData.push({label: dataElement.label, data: dataElement});
      });
    });

    this.categories = categories;
    this.defaultAttributes = allAttributes;
    this.allAttributes = allAttributes;
    this.displayedAttributes = allAttributes;
  }

  public createNewSearch(): void {
    this.store$.dispatch(new SetSelectedMySearchModal(null));
  }

  private openSearch(template: Template): void {
    this.selectedEditSearch = template;

    if (!template) {
      this.isAdmin = false;
      this.editNameControl.reset({value: '', disabled: this.isAdmin});
      this.allAttributes = [...this.defaultAttributes];
      this.selectedAttributes = [];
      this.setCategory(null);
      this.mySearchType.controls.searchType.setValue(TemplateType.FORM);
      this.mySearchType.controls.searchType.disable();

      this.cdr.markForCheck();
      return;
    }

    this.isAdmin = template.inherited;
    this.editNameControl.reset({
      value: this.selectedEditSearch.label,
      disabled: this.isAdmin
    });

    this.allAttributes = this.defaultAttributes.map((attr: Attribute) => {
      const isSelected: boolean = template.dataElementList.some((elem: DataElement) => elem.label === attr.label);
      return ({
        ...attr,
        selected: isSelected,
        disabled: attr.disabled || (template.inherited && isSelected)
      });
    });
    this.selectedAttributes = this.getSelectedAttributes(template);

    const value = (template.type === TemplateType.SEARCH || this.checkIfTemplateWithValues(template)) ?
      TemplateType.SEARCH : TemplateType.FORM;
    const disable = value === TemplateType.FORM;

    this.mySearchType.controls.searchType.setValue(value);
    this.mySearchType.controls.searchType[disable ? 'disable' : 'enable']();

    this.cdr.markForCheck();
    this.setCategory(null);
  }

  public deleteSearch(): void {
    this.confirmationService.openConfirmationModal({
      title: 'Delete Confirmation',
      message: `Please confirm you want to delete ${this.editNameControl.value}`,
      messageType: ConfirmationMessageType.NONE,
      messageTitle: '',
      confirmButtonLabel: 'Delete'
    })
      .pipe(
        filter(Boolean)
      ).subscribe(() => {
      this.store$.dispatch(new RemoveCustomMySearch(this.selectedEditSearch.templateCode));
    });
  }

  public setCategory(category: AttributesCategory): void {
    this.selectedCategory = category;
    if (category) {
      this.displayedAttributes = this.allAttributes.filter((attribute: Attribute) => attribute.name === category.id);
    } else {
      this.displayedAttributes = [...this.allAttributes];
    }
  }

  public selectAttribute(attr: Attribute): void {
    if (this.selectedEditSearch && this.selectedEditSearch.inherited) {
      this.setTemplateToDefault();
    }

    const selectedAttr: Attribute = this.allAttributes.find((a: Attribute) => a.label === attr.label);

    if (selectedAttr) {
      selectedAttr.selected = attr.selected;
    }
    if (attr.selected) {
      this.selectedAttributes = [
        ...this.selectedAttributes,
        {id: attr.name, fieldLabel: attr.label, fieldName: attr.fieldCode, tooltip: attr.label}
      ];
    } else {
      this.selectedAttributes = this.selectedAttributes
        .filter((selectedProperty: SelectionPropertiesItem) => selectedProperty.fieldName !== attr.fieldCode);
    }
  }

  private setTemplateToDefault(): void {
    this.selectedEditSearch = null;
    this.isAdmin = false;
    this.editNameControl.reset({value: '', disabled: false});
    this.allAttributes = this.allAttributes.map((a: Attribute) => ({...a, disabled: false}));
    this.setCategory(null);
    this.mySearchType.controls.searchType.setValue(TemplateType.FORM);
    this.mySearchType.controls.searchType.disable();

    this.cdr.markForCheck();
  }

  public toggleMobileNextStep(): void {
    this.isMobileNextStep = !this.isMobileNextStep;
  }

  public clearSelection(): void {
    this.selectedAttributes.forEach((selectedAttr: SelectionPropertiesItem) => {
      const attribute = this.allAttributes.find((attr: Attribute) => selectedAttr.fieldName === attr.fieldCode);
      if (attribute) {
        attribute.selected = false;
      }
    });
    this.selectedAttributes = [];
    this.displayedAttributes = [...this.displayedAttributes];
  }

  public removeAttribute(attr: SelectionPropertiesItem): void {
    const selectedAttr = this.displayedAttributes.find((displayedAttr: Attribute) => displayedAttr.fieldCode === attr.fieldName);
    if (selectedAttr) {
      selectedAttr.selected = false;
    }

    this.selectedAttributes = [...this.selectedAttributes
      .filter((selectedProperty: SelectionPropertiesItem) => selectedProperty.fieldName !== attr.fieldName)];
    this.displayedAttributes = [...this.displayedAttributes];
  }

  public saveNewSearch(): void {
    const {searchType} = this.mySearchType.value;
    const searchLabel = this.editNameControl.value;
    const previousSelectedFields = this.selectedEditSearch && this.selectedEditSearch.dataElementList ?
      this.selectedEditSearch.dataElementList.map((x) => x.fieldCode) : new Array<string>();

    if (!searchLabel) {
      this.emptyName = true;
      return;
    }

    const template: Template = {
      type: searchType,
      customTemplate: true,
      dataElementList: this.mapDataForSelectedAttributes(searchType),
      label: searchLabel
    };

    const currentSelectedFields = template.dataElementList.map((x) => x.fieldCode);

    const searchWithSameNameAndType = this.selectItemList
      .find((t: Template) => t.type === searchType && t.label === searchLabel);

    if (searchWithSameNameAndType) {
      this.store$.dispatch(new UpdateCustomMySearch({
        ...searchWithSameNameAndType,
        ...template
      }));

      this.mixpanelService.track('Search - Customize Search Click', {
        'fieldsAdded': currentSelectedFields.filter((item: string) => previousSelectedFields.indexOf(item) < 0),
        'fieldsRemoved': previousSelectedFields.filter((item: string) => currentSelectedFields.indexOf(item) < 0),
        'newSearch': false
      });
      return;
    }

    if (this.selectedEditSearch && this.selectedEditSearch.templateCode) {
      const oldTemplate = this.selectItemList.find((selectedTemplate: Template) =>
        selectedTemplate.templateCode === this.selectedEditSearch.templateCode);

      if (oldTemplate && (oldTemplate.label !== searchLabel || oldTemplate.type !== searchType)) {
        this.store$.dispatch(new SaveSelectedEditSearchFromModal(template));
        return;
      }

      this.store$.dispatch(new UpdateCustomMySearch({
        templateCode: this.selectedEditSearch.templateCode,
        ...template
      }));

    }

    this.mixpanelService.track('Search - Create New Saved Search');
    this.mixpanelService.track('Search - Customize Search Click', {
      'fieldsAdded': currentSelectedFields.filter((item: string) => previousSelectedFields.indexOf(item) < 0),
      'fieldsRemoved': previousSelectedFields.filter((item: string) => currentSelectedFields.indexOf(item) < 0),
      'newSearch': true
    });
    this.store$.dispatch(new SaveSelectedEditSearchFromModal(template));
  }

  public applyMySearch(): void {
    let template: Template;
    const {searchType} = this.mySearchType.value;
    if (this.selectedEditSearch && this.selectedEditSearch.templateCode) {
      template = {
        ...this.selectedEditSearch,
        type: searchType,
        label: this.editNameControl.value,
        dataElementList: this.mapDataForSelectedAttributes(searchType)
      };
    } else {
      template = {
        type: searchType,
        dataElementList: this.mapDataForSelectedAttributes(searchType),
        label: this.editNameControl.value || this.createDefaultNameForSearch(this.existingTemplateNames),
        templateCode: null
      };
    }

    this.store$.dispatch(new ApplyEditMySearch(template));
    this.dialogRef.close();
  }

  public close(): void {
    const currentAndDefaultsTemplates = combineLatest(
      [
        this.store$.pipe(select(selectTemplate(TemplateCode.TMPL_MY_SEARCH_DEFAULT))),
        this.store$.pipe(select(selectSelectedSearch))
      ]
    ).pipe(take(1));

    this.confirmChanges({
      confirmButtonLabel: 'Save',
      message:
        `My Search contains changes that have not been saved and can be lost.
        Please confirm you don’t want to save changes or save Search first`
    })
      .pipe(
        switchMap((modalAction: ConfirmationModalActionType) => {
          switch (modalAction) {
            case ConfirmationModalActionType.CONFIRM_WITHOUT_ACTION:
              return currentAndDefaultsTemplates
                .pipe(
                  tap(([defaultTemp, currentTemp]) => {
                    const template: Template = currentTemp && currentTemp.templateCode ? currentTemp : defaultTemp;
                    this.store$.dispatch(new SetSelectedMySearch(template));
                    this.store$.dispatch(new SetMySearchLastLoadedTemplate(template));
                    this.dialogRef.close();
                  })
                );
            case ConfirmationModalActionType.CONFIRM_WITH_ACTION:
              this.saveNewSearch();
              this.dialogRef.close();
              return of(null);
            case ConfirmationModalActionType.CLOSE:
            default:
              this.dialogRef.close();
              return of(null);
          }
        }),
      ).subscribe();
  }

  public confirmOpenSearch(template: Template): void {
    this.confirmChanges({
      confirmButtonLabel: 'Save',
      message:
        `My Search contains changes that have not been saved and can be lost.
        Please confirm you don’t want to save changes or save Search first`
    })
      .subscribe((actionType: ConfirmationModalActionType) => {
        if (!actionType) {
          this.store$.dispatch(new SetSelectedMySearchModal(template));
        }

        switch (actionType) {
          case ConfirmationModalActionType.CONFIRM_WITH_ACTION:
            this.saveNewSearch();
            return;
          case ConfirmationModalActionType.CONFIRM_WITHOUT_ACTION:
            this.store$.dispatch(new SetSelectedMySearchModal(template));
            return;
          case ConfirmationModalActionType.CLOSE:
          default:
            return;
        }
      });
  }

  private confirmChanges(options: ConfirmationMySearchMessageType = {}): Observable<ConfirmationModalActionType> {
    return this.hasChange()
      .pipe(
        switchMap((isChanged: boolean) => {
          return isChanged ?
            this.confirmationService.openConfirmationModalWithThirdAction(options) : of(null);
        }),
        take(1)
      );
  }

  private hasChange(): Observable<boolean> {
    return this.selectedEditSearch$
      .pipe(
        map((selectedTemplate: Template) => {
          if (!selectedTemplate) {
            return this.editNameControl.value !== '' || !!this.selectedAttributes.length;
          }

          if (selectedTemplate && selectedTemplate.templateCode) {
            const availableDataElements = selectedTemplate.dataElementList
              .filter((dataElem: DataElement) => {
                return dataElem.fieldCode !== FieldCode.MAP && this.allAttributes
                  .some((attribute: Attribute) => attribute.fieldCode === dataElem.fieldCode);
              });

            return this.editNameControl.value !== selectedTemplate.label
              || availableDataElements.length !== this.selectedAttributes.length
              || this.selectedAttributes.some((attribute, index) => {
                const templateDataElement: DataElement = availableDataElements[index] || {} as DataElement;
                return attribute.fieldName !== templateDataElement.fieldCode;
              });
          }

          return true;
        }),
        take(1)
      );
  }

  private getSelectedAttributes(template: Template): Array<SelectionPropertiesItem> {
    if (template) {
      return template.dataElementList.reduce((selected: Array<SelectionPropertiesItem>, elem: DataElement) => {
        const selectedAttr = this.allAttributes.find((attribute: Attribute) => attribute.fieldCode === elem.fieldCode);
        if (selectedAttr) {
          selected.push({
            id: selectedAttr.name,
            fieldLabel: selectedAttr.label,
            fieldName: selectedAttr.fieldCode,
            tooltip: selectedAttr.label
          });
        }
        return selected;
      }, []);
    }
    return [];
  }

  private mapDataForSelectedAttributes(type: TemplateType.FORM | TemplateType.SEARCH): Array<DataElement> {
    const data: Array<DataElement> = [];
    this.selectedAttributes.forEach((attr: SelectionPropertiesItem) => {
      const selected = this.attributeTemplateData.find((attrTemp: AttributeTemplateData) => attrTemp.label === attr.fieldLabel);

      if (type === TemplateType.SEARCH) {
        const oldDataElement = this.selectedEditSearch.dataElementList
          .find((dataElement: DataElement) => dataElement.label === selected.label);
        if (oldDataElement) {
          data.push(oldDataElement);
          return;
        }

        const dataElem = {...selected.data};
        const defaultOperatorValue = {
          ...dataElem.operatorValueList
            .find((operatorValue: OperatorValue) => dataElem.defaultOperator === operatorValue.operator)
        };
        defaultOperatorValue.values = [''];
        dataElem.operatorValueList = [defaultOperatorValue];

        data.push(dataElem);
        return;
      }
      data.push(selected.data);
    });

    if (type === TemplateType.SEARCH) {
      const mapDataElement = this.selectedEditSearch.dataElementList
        .find((dataElement: DataElement) => dataElement.fieldCode === FieldCode.MAP);
      if (mapDataElement) {
        data.push({...mapDataElement});
      }
    }

    return data;
  }

  private checkIfTemplateWithValues(template: Template): boolean {
    return template &&
      template.inherited !== true &&
      template.dataElementList &&
      template.dataElementList.length &&
      template.dataElementList
        .some((dataElement: DataElement) =>
          dataElement.operatorValueList &&
          dataElement.operatorValueList.length &&
          dataElement.operatorValueList
            .some((operatorValue: OperatorValue) =>
              operatorValue.values &&
              operatorValue.values.length &&
              operatorValue.values[0] !== ''));
  }

  private createDefaultNameForSearch(allNames: Array<LabelType> = []): string {
    const searchNumber = allNames.reduce((count: number, name: LabelType) => {

      if (name.label.startsWith('New Search')) {
        const nameArr = name.label.split(' ');
        // eslint-disable-next-line no-magic-numbers
        const existedCount = nameArr.length === 3 ? +nameArr[2] || 0 : 0;
        return count > existedCount ? count : existedCount + 1;
      }

      return count;
    }, 1);

    return `New Search ${Math.ceil(searchNumber)}`;
  }
}
